import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { useLocalStorage } from 'react-use'
import { useRoot } from './new/core-components/portal/RootContext'

export type ThemeName = 'light' | 'dark'
const EXPERIMENTAL_FLAG_KEY = 'CP_EXPERIMENTAL_DARK_THEME'

export const useElementTheme = (element: React.RefObject<Element | null>): { theme: ThemeName } => {
  const [active, setActive] = useState<string | undefined>(undefined)

  const mutationObserver = useThemeMutationObserver(setActive)

  useLayoutEffect(() => {
    const closestElementWithTheme = element.current?.closest('[data-theme]')
    if (closestElementWithTheme) {
      setActive(closestElementWithTheme.getAttribute('data-theme') ?? undefined)
      mutationObserver.observe(closestElementWithTheme, { attributeFilter: ['data-theme'] })
    }
    return () => mutationObserver.disconnect()
  }, [element, mutationObserver])

  return {
    theme: active === 'dark' || active === 'light' ? active : 'light',
  }
}

export const useRootTheme = (): {
  theme: ThemeName
  setTheme: (theme: ThemeName) => void
} => {
  const root = useRoot()
  const [active, setActive] = useLocalStorage<string>(EXPERIMENTAL_FLAG_KEY, undefined)

  const callEachRoot = useCallback(
    (callback: (element: Element) => void) => {
      if (root instanceof Element) {
        callback(root)
      } else {
        root.childNodes.forEach(node => {
          if (node instanceof Element) {
            callback(node)
          }
        })
      }
    },
    [root]
  )

  const mutationObserver = useThemeMutationObserver(setActive)

  useEffect(() => {
    callEachRoot(element => {
      mutationObserver.observe(element, { attributeFilter: ['data-theme'] })
    })
    return () => mutationObserver.disconnect()
  }, [callEachRoot, mutationObserver])

  const setTheme = useCallback(
    (theme: ThemeName) => {
      callEachRoot(element => {
        element.setAttribute('data-theme', theme)
      })
    },
    [callEachRoot]
  )

  return {
    theme: active === 'dark' || active === 'light' ? active : 'light',
    setTheme,
  }
}

const useThemeMutationObserver = (callback: (theme: string | undefined) => void) => {
  const [mutationObserver] = useState(
    () =>
      new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.attributeName === 'data-theme') {
            const newTheme =
              mutation.target instanceof Element ? mutation.target.getAttribute('data-theme') : ''
            callback(newTheme ?? undefined)
          }
        })
      })
  )

  return mutationObserver
}
